import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import Log, {Errors, KAError} from "@khan/logging";

import type {OfficialClarification} from "#clarifications-package/types.ts";

import type {
    ChangeablePostType,
    ContentKind,
    DiscussionsServerResponse,
    FeedbackType,
    FlagType,
    Post,
    PostKey,
    ServerPost,
    SortByType,
} from "../types.ts";
import {toGqlFeedbackType} from "../util/feedback-type-converters.ts";
import {toGqlSortEnum} from "../util/sort-type-converter.ts";

import discussionApi from "./api.ts";
import type {Action, RepliesPageStatus, State} from "./discussion-reducer.ts";
import type {ThunkAction} from "./discussion-store.ts";

type StateGetter = () => State;
type Dispatcher = (action: Action | ActionDispatcher) => void;
export type ActionDispatcher = ThunkAction;

class FetchPostError extends Error {}

export const logFetchError = (action: string, error: unknown) => {
    let errorMessage: string = "";
    let stackTrace: string = "";
    if (typeof error === "string") {
        errorMessage = error;
    } else if (error instanceof Error) {
        errorMessage = error.message;
        stackTrace = error.toString();
    }

    // This is be triggered by a network request failing altogether
    // Likely causes: network error, browser canceled request, CORS issue
    // Could be an indication that districts are blocking discussions
    // themselves rather than using our own blocking feature (see LC-1463)
    Log.error(`Failed to ${action}`, Errors.Internal, {
        sentryData: {
            contexts: {
                extras: {
                    action,
                    errorMessage,
                    stackTrace,
                },
            },
        },
    });
};

const clearPosts = (dispatch: Dispatcher, getState: StateGetter) => {
    const {currentTab} = getState();

    dispatch({
        type: "updatePosts",
        tab: currentTab,
        posts: [],
        cursor: "",
        isComplete: true, // there are no posts
        totalCount: 0,
    });
    dispatch({
        type: "clearLoadingStatus",
    });
};

export const fetchPostsAction =
    (gqlClient: ApolloGqlClient): ActionDispatcher =>
    (dispatch, getState) =>
        fetchPosts(dispatch, getState, gqlClient);

const fetchPosts = (
    dispatch: Dispatcher,
    getState: StateGetter,
    gqlClient: ApolloGqlClient,
) => {
    const state = getState();
    const {currentSort, currentTab, qaExpandKey, youtubeId, apiId} = state;

    const contentKind = state.contentKind.toLowerCase();
    const contentId = state.contentId;

    const feedbackType = toGqlFeedbackType(currentTab) as string;

    dispatch({type: "fetchingPosts"});
    discussionApi
        .get(apiId)
        .fetchPosts(
            {
                topicId: contentId,
                feedbackType: feedbackType,
                currentSort: toGqlSortEnum(currentSort),
                qaExpandKey: qaExpandKey,
                focusKind: contentKind,
            },
            gqlClient,
        )
        .catch(() => {
            throw new FetchPostError();
        })
        .then((response?: DiscussionsServerResponse | null) => {
            if (response == null) {
                return;
            }

            const {feedback, cursor, totalCount, isComplete} = response;
            const defaultDispatchData = {
                type: "updatePosts",
                tab: currentTab,
                posts: feedback,
                cursor: cursor,
                totalCount: totalCount,
                isComplete: isComplete,
            } as const;
            if (
                currentTab === "clarifications" &&
                (contentKind === "Video" ||
                    contentKind === "video" ||
                    contentKind === "youtubevideo") &&
                youtubeId
            ) {
                discussionApi
                    .get(apiId)
                    .fetchAcceptedClarifications({youtubeId})
                    .then(
                        (
                            officialClarifications: Array<OfficialClarification>,
                        ) => {
                            dispatch({
                                ...defaultDispatchData,
                                acceptedClarificationPosts:
                                    officialClarifications,
                            });
                        },
                    )
                    .catch((e) =>
                        logFetchError("fetch accepted clarifications", e),
                    );
            } else {
                dispatch(defaultDispatchData);
            }

            return response;
        })
        .catch((e) => {
            // using a custom error to differentiate between errors
            // (since we don't want to log for the initial fetching posts)
            if (!(e instanceof FetchPostError)) {
                logFetchError("while fetching posts", e);
            }
            dispatch(fetchingPostsFailed);
        });
};

export const clearAndFetchPostsAction = (
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        clearPosts(dispatch, getState);
        fetchPosts(dispatch, getState, gqlClient);
    };
};

export const initializeSortAction = (
    storedSort: SortByType,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({type: "changeSortBy", sort: storedSort});
    };
};

export const initializeContentAction =
    (
        topicSlug: string,
        contentKind: ContentKind,
        contentId: string,
        youtubeId: string | null | undefined,
        expandFeedbackType: FeedbackType | null | undefined,
        qaExpandKey: string | null | undefined,
        gqlClient: ApolloGqlClient,
    ): ActionDispatcher =>
    (dispatch: Dispatcher) => {
        dispatch({
            type: "initializeContent",
            topicSlug,
            contentKind,
            contentId,
            youtubeId,
            // @ts-expect-error - TS2322 - Type 'FeedbackType | null | undefined' is not assignable to type 'FeedbackType | undefined'.
            expandFeedbackType,
            // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
            qaExpandKey,
        });
    };

export const initializeDiscussionListComponentAction = (
    topicSlug: string,
    feedbackType: FeedbackType,
    cursor: string,
    apiId: string | null | undefined,
    isComplete: boolean,
    posts: Array<ServerPost> | null | undefined,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "initializeDiscussionListComponent",
            topicSlug,
            tab: feedbackType,
            cursor,
            apiId,
        });

        if (posts) {
            dispatch(updatePosts(feedbackType, posts, cursor, isComplete));
        } else {
            clearPosts(dispatch, getState);
            fetchPosts(dispatch, getState, gqlClient);
        }
    };
};

export const getNextPageAction = (
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const state = getState();
        const {currentSort, currentTab, qaExpandKey, apiId, tabs} = state;
        const tab = tabs[currentTab];

        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        if (!tab.cursor) {
            return;
        }

        const contentKind = state.contentKind.toLowerCase();
        const contentId = state.contentId;
        dispatch({type: "fetchingPosts"});

        discussionApi
            .get(apiId)
            .fetchPosts(
                {
                    topicId: contentId,
                    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                    cursor: tab.cursor,
                    feedbackType: toGqlFeedbackType(currentTab) as string,
                    qaExpandKey: qaExpandKey,
                    focusKind: contentKind,
                    currentSort: toGqlSortEnum(currentSort),
                },
                gqlClient,
            )
            .then((response?: DiscussionsServerResponse | null) => {
                if (response == null) {
                    return;
                }
                dispatch({
                    type: "appendPosts",
                    tab: currentTab,
                    posts: response.feedback,
                    cursor: response.cursor,
                    totalCount: response.totalCount,
                    isComplete: response.isComplete,
                });
                return response;
            })
            .catch((e) => logFetchError("get next", e));
    };
};

export const changeTabAction = (
    tab: FeedbackType,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({type: "changeTab", tab});
        const state = getState();
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        if (!state.tabs[tab].postKeys.length) {
            fetchPosts(dispatch, getState, gqlClient);
        }
    };
};

export const changeSortByAction = (
    sort: SortByType,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({type: "changeSortBy", sort});
        clearPosts(dispatch, getState);
        fetchPosts(dispatch, getState, gqlClient);
    };
};

export const resetExpandKeyAction = (): {
    type: string;
} => {
    return {type: "resetExpandKey"};
};

export const resetNewPostKeyInfoAction = (): {
    type: string;
} => {
    return {type: "resetNewPostKey"};
};

export const setFocusOnTopLevelInputAction = (): {
    type: string;
} => {
    return {type: "setFocusOnTopLevelInput"};
};

export const resetFocusOnTopLevelInputAction = (): {
    type: string;
} => {
    return {type: "resetFocusOnTopLevelInput"};
};

export const postSuccessAction = (
    post: Post,
    tab: FeedbackType,
): {
    post: Post;
    tab: FeedbackType;
    type: string;
} => {
    return {
        type: "postSuccess",
        post,
        tab,
    };
};

export const upvotePost = (
    post: {
        key: string;
        upVoted: boolean;
    },
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .upvotePost(
                {postKey: post.key, alreadyVoted: post.upVoted},
                gqlClient,
            )
            .catch((e) => logFetchError("upvote post", e));
        dispatch({type: "upvotePost", postKey: post.key});
    };
};

export const downvotePost = (
    post: {
        key: string;
        downVoted: boolean;
    },
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .downvotePost(
                {postKey: post.key, alreadyVoted: post.downVoted},
                gqlClient,
            )
            .catch((e) => logFetchError("downvote post", e));
        dispatch({type: "downvotePost", postKey: post.key});
    };
};

export const flagPost = (
    post: Post,
    flag: FlagType,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .flagPost({postKey: post.key, flag}, gqlClient)
            .then((success: boolean) => {
                if (!success) {
                    logFetchError("flag post", "unsuccessful");
                    return;
                }
                dispatch({type: "flagPost", postKey: post.key, flag});
            })
            .catch((e) => logFetchError("flag post", e));
    };
};

// NOTE: we can't use TabType here b/c those identifiers are pluralized
export const changeType = (
    post: Post,
    postType: ChangeablePostType,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();
        const intoType = toGqlFeedbackType(postType);

        discussionApi
            .get(apiId)
            .changeType({entityKey: post.key, intoType}, gqlClient)
            .catch((e) => logFetchError("change type of post", e));
        dispatch({type: "changePostType", postKey: post.key, postType});
    };
};

export const clearFlags = (
    post: Post,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .clearFlags({entityKey: post.key}, gqlClient)
            .catch((e) => logFetchError("clear flags", e));
        dispatch({type: "clearFlags", postKey: post.key});
    };
};

export const deletePost = (
    post: Post,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .deletePost({postKey: post.key}, gqlClient)
            .then((success: boolean) => {
                if (success) {
                    dispatch({type: "deletePost", postKey: post.key});
                }
            });
    };
};

export const hideProgramEverywhere = (
    programID: string,
    hide: boolean,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi.get(apiId).hideProgramEverywhere(
            {
                programID: programID,
                hide: hide,
            },
            gqlClient,
        );
    };
};

export const hideProgramFromHotlist = (
    postKey: string,
    programID: string,
    hide: boolean,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .hideProgramFromHotlist(
                {
                    programID: programID,
                    hide: hide,
                },
                gqlClient,
            )
            .then((success: boolean) => {
                if (success) {
                    dispatch({
                        type: "hideProgramFromHotlist",
                        postKey: postKey,
                        hidden: hide,
                    });
                }
            });
    };
};

export const deleteAcceptedClarificationPost = (
    clarification: OfficialClarification,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter): void => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .deleteAcceptedClarification({
                clarificationId: clarification.id,
            })
            .catch((e) => logFetchError("delete accepted clarification", e));
        dispatch({
            type: "deleteAcceptedClarificationPost",
            clarificationId: clarification.id,
        });
    };
};

export const undeletePost = (
    post: Post,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .undeletePost({entityKey: post.key}, gqlClient)
            .catch((e) => logFetchError("undelete post", e));
        dispatch({type: "undeletePost", postKey: post.key});
    };
};

export const lockThread =
    (post: Post, gqlClient: ApolloGqlClient): ActionDispatcher =>
    (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .lockThread({entityKey: post.key}, gqlClient)
            .catch((e) => logFetchError("lock thread", e));
        dispatch({type: "lockThread", postKey: post.key});
    };

export const unlockThread =
    (post: Post, gqlClient: ApolloGqlClient): ActionDispatcher =>
    (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .unlockThread({entityKey: post.key}, gqlClient)
            .catch((e) => logFetchError("unlock thread", e));
        dispatch({type: "unlockThread", postKey: post.key});
    };

export const pinThread =
    (post: Post, gqlClient: ApolloGqlClient): ActionDispatcher =>
    (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .pinThread({entityKey: post.key}, gqlClient)
            .catch((e) => logFetchError("pin thread", e));
        dispatch({type: "pinThread", postKey: post.key});
    };

export const unpinThread =
    (post: Post, gqlClient: ApolloGqlClient): ActionDispatcher =>
    (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();

        discussionApi
            .get(apiId)
            .unpinThread({entityKey: post.key}, gqlClient)
            .catch((e) => logFetchError("unpin thread", e));
        dispatch({type: "unpinThread", postKey: post.key});
    };

export const editPost = (post: Post, content: string): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        // TODO(kevinb): move PUT request from discussion-input.jsx here
        dispatch({type: "editPost", postKey: post.key, content: post.content});
    };
};

export const acceptClarificationAction = (
    // TODO(somewhatabstract, TODO-XXXX): key arg isn't used
    key: PostKey,
    // TODO(somewhatabstract, TODO-XXXX): avatarSrc arg isn't used
    avatarSrc: string,
    // TODO(somewhatabstract, TODO-XXXX): nickname arg isn't used
    nickname: string,
    kaid: string,
    content: string,
    openTimestamp: number,
    closeTimestamp: number,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter): void => {
        const {youtubeId, clarificationBeingAcceptedId, apiId} = getState();

        if (!youtubeId) {
            // TODO(diedra): Track in state that fetch failed.
            throw new KAError(
                "Can't accept a clarification if we don't know the youtubeId.",
                Errors.Internal,
            );
        }
        if (!clarificationBeingAcceptedId) {
            // TODO(diedra): Track in state that fetch failed.
            throw new KAError(
                "Can't accept a clarification if we don't have a clarificationBeingAcceptedId.",
                Errors.Internal,
            );
        }

        discussionApi
            .get(apiId)
            .acceptClarification({
                kaid,
                youtubeId,
                openTimestamp,
                closeTimestamp,
                text: content,
            })
            .then((newClarification: OfficialClarification) => {
                discussionApi
                    .get(apiId)
                    .deletePost(
                        {postKey: clarificationBeingAcceptedId},
                        gqlClient,
                    )
                    .then((success) => {
                        if (success) {
                            dispatch({
                                type: "acceptClarification",
                                // We're sending the original post key so it can be
                                // removed from the suggested clarifications list.
                                originalClarificationId:
                                    clarificationBeingAcceptedId,
                                clarification: newClarification,
                            });
                        }
                    });
            });
    };
};

export const startAcceptingClarificationAction = (
    clarificationId: string,
): ActionDispatcher => {
    return (dispatch: Dispatcher) => {
        dispatch({
            type: "startAcceptingClarification",
            clarificationId,
        });
    };
};

export const startEditingPostAction = (postKey: PostKey): ActionDispatcher => {
    return (dispatch: Dispatcher) => {
        dispatch({
            type: "startEditingPost",
            postKey,
        });
    };
};

export const editAcceptedClarificationAction = (
    postContent: string,
    openTimestamp: number,
    closeTimestamp: number,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter): void => {
        const {acceptedClarificationPosts, postBeingEditedKey, apiId} =
            getState();

        const postBeingEdited = acceptedClarificationPosts.find(
            (post: OfficialClarification) => {
                return post.id === postBeingEditedKey;
            },
        );
        if (!postBeingEdited) {
            // TODO(diedra): Track in state that fetch failed.
            throw new KAError(
                "The clarification you're trying to edit doesn't seem to exist!" +
                    `\nThe posts's key is:\n${postBeingEditedKey || "unknown"}`,
                Errors.Internal,
            );
        }

        const data = {
            ...postBeingEdited,
            openTimestamp,
            closeTimestamp,
            text: postContent,
            youtubeId: postBeingEdited.youtubeId,
        } as const;

        discussionApi
            .get(apiId)
            /**
             * TODO(jeff, LP-6882): This is expecting avatarSrc, kaid and
             * nickname at the root of the object as per the type,
             * EditAcceptedClarification_Parameters, but the type spread above
             * as `postBeingEdited` (OfficialClarification) has those values
             * in a sub-object called `reporter`. TypeScript has spotted this.
             * I didn't fix since this would constitute a behaviour change in
             * the code and therefore needs greater attention to ensure it
             * doesn't break.
             */
            // @ts-expect-error - TS2345 - Argument of type '{ openTimestamp: number; closeTimestamp: number; text: string; youtubeId: string; id: string; reporter: { id: string; kaid: string; nickname: string; avatarSrc: string; } | null | undefined; }' is not assignable to parameter of type 'EditAcceptedClarification_Parameters'.
            .editAcceptedClarification({...data})
            .then((updatedClarification: OfficialClarification) => {
                dispatch({
                    type: "editAcceptedClarification",
                    clarification: updatedClarification,
                });
                dispatch({
                    type: "stopEditingPost",
                });
            })
            // TODO(diedra): Track in state that fetch failed.
            .catch((e) => logFetchError("edit accepted clarification", e));
    };
};

export const stopAcceptingClarificationAction = (): ActionDispatcher => {
    return (dispatch: Dispatcher) => {
        dispatch({type: "stopAcceptingClarification"});
    };
};

export const stopEditingPostAction = (): ActionDispatcher => {
    return (dispatch: Dispatcher) => {
        dispatch({type: "stopEditingPost"});
    };
};

export const modifyNotificationsAction = (
    postKey: PostKey,
    isOn: boolean,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId} = getState();
        discussionApi
            .get(apiId)
            .modifyNotificationsFlag({postKey, enable: isOn}, gqlClient)
            .then((notifyOnAnswer?: boolean | null) => {
                if (notifyOnAnswer == null) {
                    logFetchError(
                        "modify notification settings",
                        "notifyOnAnswer is null",
                    );
                    return;
                }
                const post = getState().posts[postKey];
                dispatch({
                    type: "updatePost",
                    post: {...post, notifyOnAnswer},
                });
            })
            .catch((e) => logFetchError("modify notification settings", e));
    };
};

export const fetchAnswers = (
    key: string,
    gqlClient: ApolloGqlClient,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        const {apiId, fetchingAnswersStatus} = getState();

        if (
            fetchingAnswersStatus[key] === "loading" ||
            fetchingAnswersStatus[key] === "loaded"
        ) {
            return; // already fetching
        }

        const updateStatus = (status: "loading" | "loaded" | "failed") =>
            dispatch({
                type: "fetchingAnswers",
                parentKey: key,
                status,
            });

        updateStatus("loading");

        discussionApi
            .get(apiId)
            .fetchAnswers({parentKey: key}, gqlClient)
            .then((response?: Array<ServerPost> | null) => {
                if (response == null) {
                    updateStatus("failed");
                    logFetchError("fetch answers", "response is null");
                    return;
                }
                dispatch({
                    type: "updateAnswers",
                    parentKey: key,
                    answers: response,
                });
                updateStatus("loaded");
            })
            .catch((e) => {
                updateStatus("failed");
                logFetchError("fetch answers", e);
            });
    };
};

export const toggleReplyInputAction = (postKey: string): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "toggleReplyInput",
            postKey,
        });
    };
};

// Used in course-discussion.jsx
export const updatePosts = (
    tab: FeedbackType,
    posts: Array<ServerPost>,
    cursor: string,
    isComplete: boolean,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "updatePosts",
            tab,
            posts,
            cursor,
            isComplete,
            totalCount: posts.length,
        });
        // Updating the posts doesn't switch tabs so we do it manually here.
        // We don't use the changeTabAction since that also triggers loading
        // of the posts, but we can't do that since CourseDiscussion passes in
        // the posts manually since it gets them from a different endpoint.
        dispatch({
            type: "changeTab",
            tab,
        });
    };
};

export const fetchRepliesPage =
    (
        key: string,
        gqlClient: ApolloGqlClient,
        options?: {
            cursor?: string | null | undefined;
            noneToLoad?: boolean;
        },
    ): ActionDispatcher =>
    (dispatch: Dispatcher, getState: StateGetter) => {
        const updateStatus = (status: RepliesPageStatus) =>
            dispatch({
                type: "fetchingReplies",
                parentKey: key,
                status,
            });

        // noneToLoad is set when replyCount is falsy.
        // We don't need to fetch anything, so we just set the status to
        // "complete" and exit early.
        // There are cases where the replyCount is > 0 but there are no replies
        // for the user to see, and we would arrive at the same result after
        // attempting to fetch and loading an empty page of replies.
        if (options?.noneToLoad) {
            updateStatus("complete");
            return;
        }

        const {apiId, fetchingRepliesStatus} = getState();

        const status: RepliesPageStatus = fetchingRepliesStatus[key];

        if (
            status === "loading_first" ||
            status === "loading_next" ||
            status === "complete"
        ) {
            return; // already fetching or fetched
        }

        updateStatus(options?.cursor ? "loading_next" : "loading_first");

        discussionApi
            .get(apiId)
            .fetchRepliesPaginated(
                {postKey: key, cursor: options?.cursor, limit: 10},
                gqlClient,
            )
            .then(
                (
                    response?: {
                        feedback: Array<ServerPost>;
                        cursor: string | null | undefined;
                        isComplete: boolean | null | undefined;
                    } | null,
                ) => {
                    if (response == null) {
                        updateStatus("failed");
                        logFetchError("fetch replies page", "response is null");
                        return;
                    }
                    dispatch({
                        type: "updateRepliesPaginated",
                        parentKey: key,
                        replies: response.feedback,
                        repliesCursor: response.cursor,
                    });
                    updateStatus(response.isComplete ? "complete" : "partial");
                },
            )
            .catch((e) => {
                updateStatus("failed");
                logFetchError("fetch replies page", e);
            });
    };

export const selectPosts = (postKeys: Array<string>): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "selectPosts",
            postKeys,
        });
    };
};

export const skipPosts = (postKeys: Array<string>): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "skipPosts",
            postKeys,
        });
    };
};

export const deselectPost = (postKey: string): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "deselectPost",
            postKey,
        });
    };
};
export const deselectAll = (): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "deselectAll",
        });
    };
};

export const selectErroredPosts = (
    postKeys: Array<string>,
): ActionDispatcher => {
    return (dispatch: Dispatcher, getState: StateGetter) => {
        dispatch({
            type: "selectErroredPosts",
            postKeys,
        });
    };
};

export const fetchingPostsFailed: Action = {
    type: "fetchingPostsFailed",
};
